import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Brain, Zap, BarChart3, Target } from 'lucide-react';
import './FeaturesSection.css';

const FeaturesSection = () => {
  // Features 数据定义
  const features = useMemo(() => [
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "AI algorithms analyze market patterns for intelligent trading insights.",
      image: "/images/ai.png",
      detailedContent: {
        title: "Advanced AI Trading Intelligence",
        description: "Our cutting-edge machine learning models process thousands of market indicators in real-time",
        features: [
          "Pattern Recognition: Identifies profitable trading patterns with high accuracy",
          "Market Sentiment Analysis: Real-time social media and news sentiment tracking"
        ]
      },
      gradient: "from-blue-500 to-purple-600",
      color: "#6366f1"
    },
    {
      icon: Zap,
      title: "Instant Rewards",
      description: "Earn immediate rewards for your trading activities with our innovative point system.",
      image: "/images/rewards.png",
      detailedContent: {
        title: "Earn CRICKET Tokens Instantly",
        description: "Get rewarded for every prediction, win or lose. Our unique reward system incentivizes participation",
        features: [
          "Prediction Rewards: Earn CRICKET tokens per prediction",
          "Accuracy Bonus: Up to 500% bonus for consecutive wins"
        ]
      },
      gradient: "from-green-500 to-blue-500",
      color: "#10b981"
    },
    {
      icon: BarChart3,
      title: "Real-Time Data",
      description: "Access live market data and analytics to make informed trading decisions instantly.",
      image: "/images/data.png",
      detailedContent: {
        title: "Live Cricket Market Intelligence",
        description: "Connect to 15+ major exchanges for real-time price feeds and market depth analysis",
        features: [
          "Live Price Feeds: Sub-second updates from major exchanges",
          "Order Book Analysis: Deep market liquidity insights"
        ]
      },
      gradient: "from-purple-500 to-pink-500",
      color: "#8b5cf6"
    },
    {
      icon: Target,
      title: "Multi-Timeframe",
      description: "Analyze markets across multiple timeframes with our comprehensive charting tools.",
      image: "/images/chart.png",
      detailedContent: {
        title: "Advanced Chart Analysis",
        description: "Professional-grade charting tools with multiple timeframe analysis for precise market timing",
        features: [
          "Multiple Timeframes: 1m to 1W charts available",
          "Technical Indicators: 50+ built-in indicators"
        ]
      },
      gradient: "from-orange-500 to-red-500",
      color: "#f59e0b"
    }
  ], []);

  const [isVisible, setIsVisible] = useState(false);
  const [focusedCard, setFocusedCard] = useState(null); // 聚焦的卡片索引
  const [cardTransforms, setCardTransforms] = useState({}); // 存储每张卡片的transform值
  const [contentSwitched, setContentSwitched] = useState(null); // 控制内容切换时序
  const [imagesLoaded, setImagesLoaded] = useState({}); // 图片加载状态
  const [animationTriggered, setAnimationTriggered] = useState(null); // 控制渐显动画
  const sectionRef = useRef();
  const cardRefs = useRef([]); // 卡片ref数组

  // 使用 Intersection Observer 检测元素是否进入视口
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          // 当离开视口时，重置所有状态到原始状态
          setIsVisible(false);
          setFocusedCard(null);
          setContentSwitched(null);
          setCardTransforms({});
        }
      },
      { threshold: 0.3 } // 当30%的元素可见时触发
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // 预加载所有图片
  useEffect(() => {
    const preloadImages = () => {
      features.forEach((feature, index) => {
        if (feature.image) {
          const img = new Image();
          img.onload = () => {
            setImagesLoaded(prev => ({
              ...prev,
              [index]: true
            }));
          };
          img.onerror = () => {
            setImagesLoaded(prev => ({
              ...prev,
              [index]: false
            }));
          };
          img.src = feature.image;
        }
      });
    };

    preloadImages();
  }, [features]);

  // 计算卡片移动到中央的transform值
  const calculateCenterTransform = (cardIndex) => {
    const cardElement = cardRefs.current[cardIndex];
    if (!cardElement) return { translateX: 0, translateY: 0, scale: 1 };

    // 获取卡片当前位置
    const cardRect = cardElement.getBoundingClientRect();
    const cardCenterX = cardRect.left + cardRect.width / 2;
    const cardCenterY = cardRect.top + cardRect.height / 2;

    // 获取视口中央位置
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;

    // 计算从当前位置到中央的移动距离
    const translateX = viewportCenterX - cardCenterX;
    const translateY = viewportCenterY - cardCenterY;

    return {
      translateX,
      translateY,
      scale: window.innerWidth < 768 ? 1.8 : 2.5 // 移动端使用较小的缩放
    };
  };

  // 处理整个屏幕点击返回
  const handleSectionClick = (e) => {
    // 如果有聚焦的卡片，且点击的不是卡片本身，则返回原位
    if (focusedCard !== null && !e.target.closest('.feature-card')) {
      setFocusedCard(null);
      setContentSwitched(null);
      setAnimationTriggered(null);
      setCardTransforms(prev => ({
        ...prev,
        [focusedCard]: { translateX: 0, translateY: 0, scale: 1 }
      }));
    }
  };

  // 处理卡片点击
  const handleCardClick = (index, e) => {
    // 移动端禁用点击效果
    if (window.innerWidth < 768) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    if (focusedCard === index) {
      // 如果点击的是已经聚焦的卡片，则关闭
      setFocusedCard(null);
      setContentSwitched(null);
      setAnimationTriggered(null);
      setCardTransforms(prev => ({
        ...prev,
        [index]: { translateX: 0, translateY: 0, scale: 1 }
      }));
    } else {
      // 聚焦新卡片
      setFocusedCard(index);
      setContentSwitched(null);
      setAnimationTriggered(null);
      
      // 延迟切换内容和触发动画
      setTimeout(() => {
        setContentSwitched(index);
        setTimeout(() => {
          setAnimationTriggered(index);
        }, 100);
      }, 300);
      
      // 计算卡片变换
      const transform = calculateCenterTransform(index);
      setCardTransforms(prev => ({
        ...prev,
        [index]: transform
      }));
    }
  };

  return (
    <section 
      id="features"
      ref={sectionRef}
      onClick={handleSectionClick}
      className="features-section min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* 背景动画元素 */}
      <div className="floating-orbs">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>
      
      <div className="tech-lines">
        <div className="line line-1"></div>
        <div className="line line-2"></div>
        <div className="line line-3"></div>
        <div className="line line-4"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* 标题部分 - 只调整响应式尺寸 */}
        <div 
          className={`text-center mb-8 sm:mb-16 transition-all duration-500 ${
            focusedCard !== null
              ? 'opacity-0 -translate-y-8 pointer-events-none'
              : isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
          }`}
          style={{
            transitionDelay: focusedCard !== null ? '0ms' : '500ms'
          }}
        >
          <div className="text-4xl sm:text-4xl md:text-5xl lg:text-7xl font-extrabold text-gray-800 text-center mb-4 sm:mb-10">
            Why Cricket AI
          </div>
          <div className="text-base sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto text-center leading-[1.4] sm:leading-[1.6] font-light">
            Combining AI intelligence with human intuition for smarter predictions
          </div>
        </div>

        {/* 功能卡片网格 - 只调整响应式布局 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => {
            const isFocused = focusedCard === index;
            const isOtherCardFocused = focusedCard !== null && !isFocused;
            const showDetailedContent = contentSwitched === index;
            const transform = cardTransforms[index] || { translateX: 0, translateY: 0, scale: 1 };
            
            return (
              <div
                key={index}
                ref={(el) => cardRefs.current[index] = el}
                onClick={(e) => handleCardClick(index, e)}
                className={`feature-card bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg md:cursor-pointer transition-all duration-700 ${
                  isOtherCardFocused
                    ? 'opacity-0 scale-50 pointer-events-none'
                    : isFocused 
                      ? 'shadow-2xl z-50' 
                      : isVisible 
                        ? 'opacity-100 md:hover:shadow-xl md:hover:scale-105' 
                        : 'opacity-0 scale-0'
                }`}
                style={{
                  transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
                  transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: isFocused ? '0ms' : (isVisible ? `${50}ms` : '0ms'),
                  zIndex: isFocused ? 50 : 10,
                  overflow: isFocused ? 'auto' : 'hidden',
                  willChange: isFocused ? 'transform' : 'auto',
                  backfaceVisibility: 'hidden',
                  perspective: 1000
                }}
              >
                {showDetailedContent ? (
                  // 聚焦状态 - 重新设计的详细内容
                  <div className="relative overflow-hidden">
                    {/* 背景装饰 */}
                    <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 opacity-10">
                      <div className={`w-full h-full rounded-full bg-gradient-to-br ${feature.gradient}`}></div>
                    </div>
                    
                    {/* 顶部区域 */}
                    <div className="relative z-10 flex items-start justify-between mb-3 sm:mb-6">
                      <div className="flex items-center space-x-3 sm:space-x-4"> 
                        <div 
                          className={`rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center w-8 sm:w-10 h-8 sm:h-10 text-white shadow-lg flex-shrink-0`}
                        >
                          <feature.icon size={window.innerWidth < 640 ? 14 : 18} />
                        </div>
                        <div className="flex-1 text-left justify-between">
                          <div className="text-base sm:text-lg font-bold text-gray-900">
                            {feature.detailedContent.title}
                          </div>
                          <div className="text-xs sm:text-[9px] text-gray-500">
                            {feature.description}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 下部内容区域 - 移动端垂直布局，桌面端左右布局 */}
                    <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 items-center">
                      {/* 左侧图片区域 */}
                      <div className="w-full sm:flex-1">
                        <div className="rounded-lg h-28 sm:h-32 overflow-hidden bg-gray-100 relative">
                          {imagesLoaded[index] === true ? (
                            <img 
                              src={feature.image} 
                              alt={feature.title}
                              className="w-full h-full object-cover"
                              style={{ 
                                opacity: animationTriggered === index ? 1 : 0,
                                transform: animationTriggered === index 
                                  ? 'scale(1) translateY(0px)' 
                                  : 'scale(1.1) translateY(10px)',
                                transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                transitionDelay: '0.1s'
                              }}
                            />
                          ) : imagesLoaded[index] === false ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-gray-400 text-xs">Image not available</div>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="animate-pulse bg-gray-200 w-full h-full rounded-lg"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* 右侧Key Features */}
                      <div className="w-full sm:flex-1">
                        <div className="space-y-2 sm:space-y-4">
                          {feature.detailedContent.features.map((item, idx) => (
                            <div 
                              key={idx} 
                              className="flex items-start space-x-2 py-1 px-2 rounded-lg relative overflow-hidden"
                              style={{
                                opacity: animationTriggered === index ? 1 : 0,
                                transform: animationTriggered === index 
                                  ? 'translateX(0px) translateY(0px)' 
                                  : 'translateX(20px) translateY(10px)',
                                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                transitionDelay: `${0.2 + idx * 0.1}s`
                              }}
                            >
                              <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-70`}></div>
                              <div className="relative z-10 w-full">
                                <div 
                                  className="text-white text-xs sm:text-[10px] leading-relaxed text-left"
                                  style={{ 
                                    fontWeight: '100',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif'
                                  }}
                                >
                                  {item}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // 正常状态 - 显示简要内容
                  <div className="text-center">
                    <div 
                      className={`rounded-full bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-4 sm:mb-6 mx-auto w-10 sm:w-16 h-10 sm:h-16 text-white`}
                    >
                      <feature.icon size={window.innerWidth < 640 ? 16 : 24} />
                    </div>
                    <div className="font-bold text-gray-900 mb-2 sm:mb-4 text-base sm:text-xl">
                      {feature.title}
                    </div>
                    <div className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {feature.description}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
